import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReportTemplate } from "../../types/ReportTemplate";
import {
	updateTemplate,
	deleteTemplate,
} from "../../utils/templateStorage";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { format } from "date-fns";
import { getTranslatedError } from "../../utils/translateError";

interface TemplateDropdownProps {
	currentTemplateId: string | null;
	onLoadTemplate: (template: ReportTemplate) => void;
	onClearTemplate: () => void;
	templates: ReportTemplate[];
	loadingTemplates: boolean;
	onRefreshTemplates: () => Promise<void>;
}

const TemplateDropdown: React.FC<TemplateDropdownProps> = ({
	currentTemplateId,
	onLoadTemplate,
	onClearTemplate,
	templates,
	loadingTemplates: loading,
	onRefreshTemplates,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [showRenameModal, setShowRenameModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
	const [renameValue, setRenameValue] = useState("");
	const [renameError, setRenameError] = useState<string | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const currentTemplate = templates.find((t) => t.id === currentTemplateId);

	const handleLoadTemplate = (template: ReportTemplate) => {
		onLoadTemplate(template);
		setIsOpen(false);
	};

	const handleRenameClick = (template: ReportTemplate) => {
		setSelectedTemplate(template);
		setRenameValue(template.name);
		setRenameError(null);
		setShowRenameModal(true);
		setIsOpen(false);
	};

	const handleRenameSave = async () => {
		if (!selectedTemplate || !renameValue.trim()) {
			setRenameError(t("errors.templateNameRequired"));
			return;
		}

		try {
			setRenameError(null);
			await updateTemplate(selectedTemplate.id, { name: renameValue.trim() });
			await onRefreshTemplates();
			setShowRenameModal(false);
			setSelectedTemplate(null);
			setRenameValue("");
			setRenameError(null);
		} catch (err) {
			setRenameError(getTranslatedError(err, "errors.renameTemplateFailed"));
		}
	};

	const handleDeleteClick = (template: ReportTemplate) => {
		setSelectedTemplate(template);
		setShowDeleteConfirm(true);
		setIsOpen(false);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedTemplate) return;

		try {
			await deleteTemplate(selectedTemplate.id);
			await onRefreshTemplates();

			if (selectedTemplate.id === currentTemplateId) {
				onClearTemplate();
			}

			setShowDeleteConfirm(false);
			setSelectedTemplate(null);
		} catch (err) {
			console.error("Failed to delete template:", err);
		}
	};

	const formatDate = (dateString: string) => {
		try {
			return format(new Date(dateString), "MMM dd, yyyy");
		} catch {
			return dateString;
		}
	};

	return (
		<>
			<div className="w-full" ref={wrapperRef}>
				<label className="block text-gray-700 text-sm font-bold mb-2">
					{t("form.template.savedTemplates")}
				</label>

				<div className="relative">
					<button
						type="button"
						onClick={() => setIsOpen(!isOpen)}
						className={`shadow appearance-none border rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent flex items-center justify-between transition-all duration-200 ${
							currentTemplate
								? "bg-primary-50 border-primary-300"
								: "bg-white border-gray-300"
						}`}
					>
						<span>
							{currentTemplate
								? currentTemplate.name
								: t("form.template.loadTemplate")}
						</span>
						<svg
							className={`w-5 h-5 transition-transform ${
								isOpen ? "transform rotate-180" : ""
							}`}
							fill="none"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path d="M19 9l-7 7-7-7"></path>
						</svg>
					</button>

					{isOpen && (
						<div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
							{loading ? (
								<div className="px-4 py-3 text-gray-500 text-sm text-center">
									{t("form.template.loadingTemplates")}
								</div>
							) : templates.length === 0 ? (
								<div className="px-4 py-3 text-gray-500 text-sm">
									{t("form.template.noTemplates")}
								</div>
							) : (
								<ul className="py-1">
									{templates.map((template) => (
										<li
											key={template.id}
											className={`px-4 py-2 hover:bg-primary-50/50 ${
												template.id === currentTemplateId
													? "bg-primary-50"
													: ""
											}`}
										>
											<div className="flex items-center justify-between">
												<div className="flex-1 min-w-0">
													<div className="font-medium text-gray-900 truncate">
														{template.name}
													</div>
													<div className="text-xs text-gray-500">
														{t("form.template.created", {
															date: formatDate(template.createdAt),
														})}
													</div>
												</div>
												<div className="flex items-center gap-1 ml-2">
													{template.id !== currentTemplateId && (
														<button
															type="button"
															onClick={() =>
																handleLoadTemplate(template)
															}
															className="px-2 py-1 text-xs text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
															title={t("form.template.loadTooltip")}
														>
															{t("form.template.load")}
														</button>
													)}
													<button
														type="button"
														onClick={() =>
															handleRenameClick(template)
														}
														className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
														title={t("form.template.renameTooltip")}
													>
														{t("form.template.rename")}
													</button>
													<button
														type="button"
														onClick={() =>
															handleDeleteClick(template)
														}
														className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
														title={t("form.template.deleteTooltip")}
													>
														{t("form.template.delete")}
													</button>
												</div>
											</div>
										</li>
									))}
								</ul>
							)}
							{currentTemplate && (
								<div className="border-t border-gray-200">
									<button
										type="button"
										onClick={() => {
											onClearTemplate();
											setIsOpen(false);
										}}
										className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
									>
										{t("form.template.clearSelection")}
									</button>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			<Modal
				isOpen={showRenameModal}
				onClose={() => {
					setShowRenameModal(false);
					setSelectedTemplate(null);
					setRenameValue("");
					setRenameError(null);
				}}
				title={t("form.template.renameModalTitle")}
			>
				<div className="space-y-4">
					<Input
						label={t("form.template.templateName")}
						value={renameValue}
						onChange={(e) => {
							setRenameValue(e.target.value);
							setRenameError(null);
						}}
						error={renameError || undefined}
						autoFocus
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleRenameSave();
							}
						}}
					/>

					<div className="flex justify-end gap-3 pt-4">
						<Button
							variant="secondary"
							onClick={() => {
								setShowRenameModal(false);
								setSelectedTemplate(null);
								setRenameValue("");
								setRenameError(null);
							}}
						>
							{t("common.cancel")}
						</Button>
						<Button
							variant="primary"
							onClick={handleRenameSave}
							disabled={!renameValue.trim()}
						>
							{t("common.save")}
						</Button>
					</div>
				</div>
			</Modal>

			<Modal
				isOpen={showDeleteConfirm}
				onClose={() => {
					setShowDeleteConfirm(false);
					setSelectedTemplate(null);
				}}
				title={t("form.template.deleteModalTitle")}
			>
				<div className="space-y-4">
					<p className="text-gray-600">
						{t("form.template.deleteConfirm", {
							name: selectedTemplate?.name ?? "",
						})}
					</p>

					<div className="flex justify-end gap-3 pt-4">
						<Button
							variant="secondary"
							onClick={() => {
								setShowDeleteConfirm(false);
								setSelectedTemplate(null);
							}}
						>
							{t("common.cancel")}
						</Button>
						<Button variant="danger" onClick={handleDeleteConfirm}>
							{t("common.delete")}
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default TemplateDropdown;
